import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { Alert } from "@mui/material";
import * as React from "react";

import { useNavigation } from "@/providers";
import { EntityCustomFields } from "@/shared/components/CustomFields";
import { AlertDialog, RobotoButton } from "@/shared/components/index.tsx";
import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { TagsEditor } from "@/shared/components/TagsEditor";
import { UserLink } from "@/shared/components/UserLink";
import { FormattedDateTime } from "@/shared/datetime";
import { useDeleteCollection } from "@/shared/domain/collections/hooks/useDeleteCollection";
import { useUpdateCollection } from "@/shared/domain/collections/hooks/useUpdateCollection";
import { TargetEntityType } from "@/shared/domain/customFields";

import styles from "./CollectionDetailsTab.module.css";
import { useCollectionOutletContext } from "./useCollectionOutletContext";

export const CollectionDetailsTab: React.FC = () => {
  const collection = useCollectionOutletContext();
  const { goto } = useNavigation();

  const { mutateAsync: updateCollection } = useUpdateCollection();
  const { mutateAsync: deleteCollection, error: deleteError } =
    useDeleteCollection();

  const [updateError, setUpdateError] = React.useState<Error | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  const updateTags = (updates: {
    add_tags?: string[];
    remove_tags?: string[];
  }) =>
    updateCollection({
      collectionId: collection.collection_id,
      resourceOwnerId: collection.org_id,
      updates,
    });

  return (
    <div className={styles.detailsGrid}>
      {(updateError ?? deleteError) && (
        <Alert severity="error" style={{ gridColumn: "1 / -1" }}>
          {(updateError ?? deleteError)!.message}
        </Alert>
      )}

      <span className={styles.detailLabel}>Created</span>
      <span className={styles.detailValue}>
        <FormattedDateTime value={collection.created} /> by{" "}
        <UserLink
          userId={collection.created_by}
          linkSx={{ fontSize: "var(--font-size-sm)" }}
        />
      </span>

      <span className={styles.detailLabel}>Updated</span>
      <span className={styles.detailValue}>
        <FormattedDateTime value={collection.updated} /> by{" "}
        <UserLink
          userId={collection.updated_by}
          linkSx={{ fontSize: "var(--font-size-sm)" }}
        />
      </span>

      <span className={styles.detailLabel}>Tags</span>
      <TagsEditor
        tags={collection.tags}
        recordKey={collection.collection_id}
        autocompleteType={AutocompleteType.CollectionTags}
        onAdd={(tag) => updateTags({ add_tags: [tag] })}
        onRemove={(tag) => updateTags({ remove_tags: [tag] })}
        analytics={{
          add: "AddCollectionTagClicked",
          remove: "RemoveCollectionTagClicked",
          props: { collectionId: collection.collection_id },
        }}
      />

      <div style={{ gridColumn: "1 / -1" }}>
        <EntityCustomFields
          entityType={TargetEntityType.Collection}
          orgId={collection.org_id}
          values={collection.custom_fields}
          commit={(changeset) =>
            updateCollection({
              collectionId: collection.collection_id,
              resourceOwnerId: collection.org_id,
              updates: { custom_fields_changeset: changeset },
            })
          }
          onError={setUpdateError}
          variant="plain"
        />
      </div>

      <span className={styles.detailLabel}>Operations</span>
      <div>
        <RobotoButton
          eventName="DeleteCollectionClicked"
          eventProperties={{ collectionId: collection.collection_id }}
          variant="outlined"
          size="small"
          color="error"
          startIcon={<DeleteOutlinedIcon />}
          onClick={() => setDeleteDialogOpen(true)}
        >
          Delete Collection
        </RobotoButton>
      </div>

      <AlertDialog
        dialogOpen={deleteDialogOpen}
        handleClose={() => setDeleteDialogOpen(false)}
        dialogTitle="Delete Collection"
        dialogText={`Are you sure you want to delete ${collection.name ? `${collection.name} (${collection.collection_id})` : collection.collection_id}?`}
        dialogAction={async () => {
          await deleteCollection({
            collectionId: collection.collection_id,
            orgId: collection.org_id,
            resourceOwnerId: collection.org_id,
          });
          goto.collections();
        }}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
};
